// src/components/DashboardLoader.jsx
// Holds the loaded workbook and everything parsed from it, and routes every
// signed-in page through the shared AppShell (sidebar + page area).
import React, { Suspense, lazy, useCallback, useMemo, useState } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import HomePage from "./HomePage";
import LoginPage from "./LoginPage";
import UploadPage from "../pages/UploadPage";
import AppShell, { AUTH_STORAGE_KEY } from "../ui/AppShell";
import heroList from "../data/heroList.json";
import { getHeroImageUrl } from "../utils/heroCatalog";
import {
  parseGamesFromWorkbook,
  parseComboStatsFromExcel,
  parseScrimStatsFromWorkbook,
} from "../utils/excelParser.js";
import { parseTeamPerformanceFromFile } from "../utils/teamPerformancePipeline";
import { buildComboStatsFromGames, mergeComboStats } from "../utils/comboStatsFromGames";
import { playedDates } from "../utils/dashboardDates";
import * as XLSX from "xlsx";

// Big pages load their code when first opened, so the first screen stays light.
const DraftPage = lazy(() => import("./DraftPage"));
const WinRateTierSelection = lazy(() => import("./WinRateTierSelection"));
const TierData = lazy(() => import("./tier/TierData"));
const MetaLab = lazy(() => import("./MetaLab"));
const TeamPerformancePage = lazy(() => import("../pages/TeamPerformancePage"));
const DraftRecapPage = lazy(() => import("../pages/DraftRecapPage"));
const TacticBoardPage = lazy(() => import("../pages/TacticBoardPage"));
const MinimapLabPage = lazy(() => import("../pages/MinimapLabPage"));
const ChartsPage = lazy(() => import("../pages/ChartsPage"));

function PageLoading() {
  return (
    <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "var(--text-3)", fontSize: 13 }}>
      <div style={{ display: "grid", justifyItems: "center", gap: 12 }}>
        <div className="ds-skeleton" style={{ width: 180, height: 8 }} />
        กำลังโหลดหน้า…
      </div>
    </div>
  );
}

const lazyPage = (node) => <Suspense fallback={<PageLoading />}>{node}</Suspense>;

export default function DashboardLoader() {
  // ----- ข้อมูลหลัก -----
  const [games, setGames] = useState([]);
  const [homeGames, setHomeGames] = useState([]);
  const [sheetComboStats, setSheetComboStats] = useState(null);
  const [dropdownDates, setDropdownDates] = useState([]);

  // ----- เก็บไฟล์/เวิร์กบุ๊ก -----
  const [excelFile, setExcelFile] = useState(null);
  const [excelWb, setExcelWb] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);

  // ----- UI -----
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // ชีท Combo_Stats เก็บคอมโบไม่ครบ (ไฟล์ปัจจุบัน: 41 คู่ จาก 216 คู่ที่เล่นจริง และ
  // ไม่มี 3 ตัวเลย) เลยนับจาก log เป็นหลัก แล้วใช้ชีทเป็นตัวสำรอง — ทุกหน้าจะได้ชุดเดียวกัน
  const comboStats = useMemo(
    () => mergeComboStats(buildComboStatsFromGames(games), sheetComboStats),
    [games, sheetComboStats]
  );

  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem(AUTH_STORAGE_KEY) === "true";

  const parseWorkbookFile = useCallback(
    async (file) => {
      if (!file) return;
      setUploadError("");
      setIsProcessingUpload(true);
      let wb;
      try {
        const buf = await file.arrayBuffer();
        wb = XLSX.read(new Uint8Array(buf), { type: "array" });
      } catch (err) {
        console.error("Failed to read workbook:", err);
        setUploadError("ไฟล์นี้ไม่ใช่ .xlsx ที่อ่านได้");
        setIsProcessingUpload(false);
        return;
      }

      setExcelFile(file);
      setExcelWb(wb);
      // Home overview reads All_Scrim (same sheet as Team Performance) so the two
      // headline win-rate figures agree, instead of the recent APL_Scrim block.
      setHomeGames(parseScrimStatsFromWorkbook(wb, { preferAllScrim: true }));
      setGames(parseGamesFromWorkbook(wb));

      parseComboStatsFromExcel(file, setSheetComboStats, (dropdowns) => {
        if (dropdowns?.dates) setDropdownDates(dropdowns.dates);
      });

      setIsProcessingUpload(false);
      navigate("/dashboard");

      // The Team Performance report is the slowest part; it finishes in the background.
      setTeamPerformance(null);
      try {
        const perf = await parseTeamPerformanceFromFile(file, {
          includeOther: false,
          minGamesForDelta: 3,
        });
        setTeamPerformance(perf);
      } catch (err) {
        console.error("Team performance parse failed:", err);
        setTeamPerformance(null);
      }
    },
    [navigate]
  );

  // What the sidebar and the upload page say about the loaded file.
  const workbook = useMemo(() => {
    if (!excelFile) return null;
    const { latest } = playedDates(homeGames);
    const all = homeGames.map((g) => String(g.Date)).filter((d) => /^\d{8}$/.test(d)).sort();
    return {
      name: excelFile.name,
      sheets: excelWb?.SheetNames || [],
      games: homeGames.length,
      draftGames: games.length,
      combos: (comboStats?.combo2?.length || 0) + (comboStats?.combo3?.length || 0),
      first: all[0] || "",
      latest,
      opponents: new Set(homeGames.map((g) => g.competitor).filter((c) => c && c !== "ไม่พบข้อมูล")).size,
    };
  }, [excelFile, excelWb, homeGames, games, comboStats]);

  const guarded = isAuthenticated ? <AppShell workbook={workbook} /> : <Navigate to="/" replace />;

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />

      <Route element={guarded}>
        <Route
          path="/upload"
          element={<UploadPage onFile={parseWorkbookFile} processing={isProcessingUpload} error={uploadError} workbook={workbook} />}
        />
        <Route
          path="/dashboard"
          element={<HomePage games={homeGames} comboStats={comboStats} dropdownDates={dropdownDates} sourceName={excelFile?.name} />}
        />
        <Route
          path="/draft"
          element={lazyPage(
            <DraftPage
              heroes={heroList}
              comboStats={comboStats}
              excelData={excelWb}
              excelFileBlob={excelFile}
              games={games}
              dropdownDates={dropdownDates}
            />
          )}
        />
        <Route path="/win-rate" element={lazyPage(<WinRateTierSelection games={games} />)} />
        <Route path="/meta-lab" element={lazyPage(<MetaLab />)} />
        <Route
          path="/team-performance"
          element={lazyPage(
            <TeamPerformancePage teamPerformance={teamPerformance} comboStats={comboStats} excelFile={excelFile} games={games} />
          )}
        />
        <Route path="/draft-recap" element={lazyPage(<DraftRecapPage games={games} excelFile={excelFile} />)} />
        <Route path="/tactic-board" element={lazyPage(<TacticBoardPage games={games} excelFile={excelFile} />)} />
        <Route path="/minimap-lab" element={lazyPage(<MinimapLabPage games={games} excelFile={excelFile} />)} />
        <Route
          path="/charts"
          element={lazyPage(<ChartsPage scrimGames={homeGames} draftGames={games} sourceName={excelFile?.name} />)}
        />
        <Route
          path="/tier-data"
          element={lazyPage(
            <TierData
              games={games}
              comboStats={comboStats}
              dropdownDates={dropdownDates}
              excelFile={excelFile}
              heroImageOf={getHeroImageUrl}
            />
          )}
        />
      </Route>

      <Route path="*" element={<Navigate to={isAuthenticated ? "/upload" : "/"} replace />} />
    </Routes>
  );
}
